/**
 * 1.6.5 §2 — "would anything be lost if the page reloaded now?" A small registry the app asks before it updates
 * itself (lib/app-update.ts):
 *
 *  - a form says so itself: `useDirtyFlag('finish', isDirty)` in the Finish, Book and Arrived sheets and the People
 *    edit form (an open sheet already counts as "not now"; the flag is the form's own word on top of that);
 *  - anything typed or chosen anywhere on the page since the person came to it (`watchTyping`, one listener on the
 *    document) — so a form nobody registered, a link page or a half-written search is never thrown away. Going to
 *    another page clears it (`clearTyped`): what was typed went with the page.
 */
import { useEffect } from 'react';

const flags = new Map<string, boolean>();
let typed = false;

export function setDirty(key: string, dirty: boolean): void {
  if (dirty) flags.set(key, true);
  else flags.delete(key);
}

/** Is any registered form dirty, or has something been typed on this page? */
export function anyDirty(): boolean {
  return typed || flags.size > 0;
}

/** A form's own word: dirty while `isDirty`, clean again when it is not — or when the form goes away. */
export function useDirtyFlag(key: string, isDirty: boolean): void {
  useEffect(() => {
    setDirty(key, isDirty);
    return () => setDirty(key, false);
  }, [key, isDirty]);
}

/** Notes anything typed or chosen on the page (the person's own input events only). */
export function watchTyping(doc: Document = document): () => void {
  const note = () => {
    typed = true;
  };
  doc.addEventListener('input', note, true);
  doc.addEventListener('change', note, true);
  return () => {
    doc.removeEventListener('input', note, true);
    doc.removeEventListener('change', note, true);
  };
}

/** The person went to another page: what was typed on the last one is gone with it. */
export function clearTyped(): void {
  typed = false;
}

/** Tests only. */
export function resetDirty(): void {
  flags.clear();
  typed = false;
}
